import React from 'react';
import { useSelector } from 'react-redux';
import { selectOfficeObj } from '../../redux/slices/office-slice.js';
import { black, red } from '../../styles/standard-colors.js';
import convertPhoneNumberToNationalFormat from '../../utils/convert-phone-number-to-national.js';

/**
 * Component for displaying office information in a table format.
 * @returns {JSX.Element} JSX element representing the Office Information Table.
 */
const OfficeInfoTable = () => {
    const officeObj = useSelector(selectOfficeObj);

    const connectionColor =
        officeObj.numOfDBConnections ||
        officeObj.numOfFSConnections ||
        officeObj.numOfIVConnections ||
        officeObj.numOfPPConnections ||
        officeObj.connectionSoftware === 'cloud'
            ? black
            : red;
    const eobColor =
        officeObj.eobPath === '' || officeObj.eobPath === null ? red : black;
    const hubColor = officeObj.hubId ? black : red;
    const kocColor = officeObj.isKoc ? black : red;

    const dsColor =
        officeObj.dentalSoftware === '' || officeObj.dentalSoftware === null
            ? red
            : black;
    const eClaimsColor =
        officeObj.eClaims === '' || officeObj.eClaims === null ? red : black;
    const xraysColor =
        officeObj.xrays === '' || officeObj.xrays === null ? red : black;
    const backupTimesColor =
        officeObj.backupTimes === '' || officeObj.backupTimes === null
            ? red
            : black;
    const renderTextCell = (value) => value || 'N/A';

    // console.log('log onj is:- ', officeObj);

    return (
        <>
            <table className="office-table">
                <tbody>
                    <tr>
                        <td className="office-table-left-cell">Notes:</td>
                        <td
                            className="office-table-right-cell"
                            style={{ whiteSpace: 'pre-wrap' }}>
                            {officeObj.notes}
                        </td>
                    </tr>
                    <tr style={{ padding: '.25em' }}>
                        <td
                            className="office-table-left-cell"
                            style={{ color: hubColor }}>
                            Linked to hub office?:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.hubId ? 'Yes' : 'No'}
                        </td>
                    </tr>
                    <tr style={{ padding: '.25em' }}>
                        <td className="office-table-left-cell">Office Name:</td>
                        <td className="office-table-right-cell">
                            {officeObj.officeName}
                        </td>
                    </tr>
                    <tr>
                        <td className="office-table-left-cell">
                            Phone Number:
                        </td>
                        <td className="office-table-right-cell">
                            {convertPhoneNumberToNationalFormat(
                                officeObj.phoneNumber
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td className="office-table-left-cell">Email:</td>
                        <td className="office-table-right-cell">
                            {officeObj.officeEmail}
                        </td>
                    </tr>
                    <tr>
                        <td className="office-table-left-cell">
                            Office Hours:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.officeHours}
                        </td>
                    </tr>
                    <tr>
                        <td className="office-table-left-cell">POC:</td>
                        <td className="office-table-right-cell">
                            {officeObj.poc}
                        </td>
                    </tr>
                    <tr>
                        <td className="office-table-left-cell">IT Name:</td>
                        <td className="office-table-right-cell">
                            {officeObj.itName}
                        </td>
                    </tr>
                    <tr>
                        <td className="office-table-left-cell">IT Email:</td>
                        <td className="office-table-right-cell">
                            {officeObj.itEmail}
                        </td>
                    </tr>
                    <tr>
                        <td className="office-table-left-cell">
                            IT Phone Number:
                        </td>
                        <td className="office-table-right-cell">
                            {convertPhoneNumberToNationalFormat(
                                officeObj.itPhoneNumber
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td className="office-table-left-cell">Production:</td>
                        <td className="office-table-right-cell">
                            {officeObj.production}
                        </td>
                    </tr>
                    <tr>
                        <td className="office-table-left-cell">
                            Scanner Info:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.scannerInfo}
                        </td>
                    </tr>
                    <tr>
                        <td
                            className="office-table-left-cell"
                            style={{ color: eobColor }}>
                            EOB Path:
                        </td>
                        <td
                            className="office-table-right-cell"
                            style={{ color: eobColor }}>
                            {officeObj.eobPath}
                        </td>
                    </tr>

                    <tr>
                        <td className="office-table-left-cell">
                            Install Date:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.installDate
                                ? `${
                                      new Date(
                                          officeObj.installDate
                                      ).getUTCMonth() + 1
                                  }/${new Date(
                                      officeObj.installDate
                                  ).getUTCDate()}/${new Date(
                                      officeObj.installDate
                                  ).getUTCFullYear()}`
                                : ''}
                        </td>
                    </tr>
                    <tr>
                        <td className="office-table-left-cell">
                            Install Tech:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.installTech}
                        </td>
                    </tr>
                    <tr>
                        <td
                            className="office-table-left-cell"
                            style={{ color: connectionColor }}>
                            Number of DB Connections:
                        </td>
                        <td
                            className="office-table-right-cell"
                            style={{ color: connectionColor }}>
                            {officeObj.numOfDBConnections
                                ? officeObj.numOfDBConnections
                                : 0}
                        </td>
                    </tr>
                    <tr>
                        <td
                            className="office-table-left-cell"
                            style={{ color: connectionColor }}>
                            Number of IV Connections:
                        </td>
                        <td
                            className="office-table-right-cell"
                            style={{ color: connectionColor }}>
                            {officeObj.numOfIVConnections
                                ? officeObj.numOfIVConnections
                                : 0}
                        </td>
                    </tr>
                    <tr>
                        <td
                            className="office-table-left-cell"
                            style={{ color: connectionColor }}>
                            Number of PB Connections:
                        </td>
                        <td
                            className="office-table-right-cell"
                            style={{ color: connectionColor }}>
                            {officeObj.numOfPPConnections
                                ? officeObj.numOfPPConnections
                                : 0}
                        </td>
                    </tr>

                    {/* <tr>
                        <td
                            className="office-table-left-cell"
                            style={{ color: connectionColor }}>
                            Number of FS Connections:
                        </td>
                        <td
                            className="office-table-right-cell"
                            style={{ color: connectionColor }}>
                            {officeObj.numOfFSConnections
                                ? officeObj.numOfFSConnections
                                : 0}
                        </td>
                    </tr> */}
                    <tr>
                        <td className="office-table-left-cell">
                            Connection Software:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.connectionSoftware}
                        </td>
                    </tr>
                    <tr>
                        <td className="office-table-left-cell">
                            Unique VPN Addendum on File:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.uniqueVPN &&
                            officeObj.uniqueVPN.addendumOnFile
                                ? 'Yes'
                                : 'No'}
                        </td>
                    </tr>
                    <tr>
                        <td
                            className="office-table-left-cell"
                            style={{ color: dsColor }}>
                            Dental Sofware:
                        </td>
                        <td
                            className="office-table-right-cell"
                            style={{ color: dsColor }}>
                            {officeObj.dentalSoftware}
                        </td>
                    </tr>

                    <tr>
                        <td className="office-table-left-cell">
                            Dental Software URL:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.dsURL}
                        </td>
                    </tr>
                    <tr>
                        <td
                            className="office-table-left-cell"
                            style={{ color: eClaimsColor }}>
                            eClaims & Attachments:
                        </td>
                        <td
                            className="office-table-right-cell"
                            style={{ color: eClaimsColor }}>
                            {officeObj.eClaims}
                        </td>
                    </tr>

                    <tr>
                        <td
                            className="office-table-left-cell"
                            style={{ color: xraysColor }}>
                            X-Rays:
                        </td>
                        <td
                            className="office-table-right-cell"
                            style={{ color: xraysColor }}>
                            {officeObj.xrays}
                        </td>
                    </tr>
                    <tr>
                        <td
                            className="office-table-left-cell"
                            style={{ color: backupTimesColor }}>
                            Backup Times:
                        </td>
                        <td
                            className="office-table-right-cell"
                            style={{ color: backupTimesColor }}>
                            {officeObj.backupTimes}
                        </td>
                    </tr>
                    <tr>
                        <td className="office-table-left-cell">
                            Firewall Info:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.firewallInfo}
                        </td>
                    </tr>

                    <tr>
                        <td className="office-table-left-cell">
                            Zilla Number:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.twilioActivated
                                ? convertPhoneNumberToNationalFormat(
                                      officeObj.twilioNumber
                                  )
                                : 'N/A'}
                        </td>
                    </tr>
                    {/* <tr>
                        <td className="office-table-left-cell">
                            Called Id Number:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.callerIdVerified
                                ? convertPhoneNumberToNationalFormat(
                                      officeObj.callerIdNumber
                                  )
                                : 'N/A'}
                        </td>
                    </tr> */}
                    <tr>
                        <td className="office-table-left-cell">
                            Caller ID Verified:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.callerIdVerified ? 'Yes' : 'No'}
                        </td>
                    </tr>
                    <tr>
                        <td className="office-table-left-cell">
                            Zilla Business Profile status:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.businessProfile?.status}
                        </td>
                    </tr>
                    <tr>
                        <td
                            className="office-table-left-cell"
                            style={{ color: kocColor }}>
                            Is Auth for KOC:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.isKoc ? 'Yes' : 'No'}
                        </td>
                    </tr>
                    <tr>
                        <td className="office-table-left-cell">
                            Is Copilot Office?:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.isCopilotOffice ? 'Yes' : 'No'}
                        </td>
                    </tr>
                    <tr>
                        <td className="office-table-left-cell">
                            Is Companion office?:
                        </td>
                        <td className="office-table-right-cell">
                            {officeObj.isCompanionOffice ? 'Yes' : 'No'}
                        </td>
                    </tr>
                    {!officeObj.hubId && (
                        <tr>
                            <td
                                colSpan={2}
                                style={{
                                    fontSize: 13,
                                    color: '#666',
                                    fontStyle: 'italic',
                                    padding: '.5em'
                                }}>
                                Office not linked to Hub2 — KOC notes won&apos;t
                                sync.
                            </td>
                        </tr>
                    )}
                    {!officeObj.isCopilotOffice &&
                        officeObj.isCompanionOffice && (
                            <>
                                <tr>
                                    <td className="office-table-left-cell">
                                        Super Sync Computer:
                                    </td>
                                    <td className="office-table-right-cell">
                                        {renderTextCell(
                                            officeObj.superSyncComputer
                                        )}
                                    </td>
                                </tr>
                            </>
                        )}
                    {officeObj.isCopilotOffice && (
                        <>
                            <tr style={{ padding: '.25em' }}>
                                <td className="office-table-left-cell">
                                    Copilot Server Name:
                                </td>
                                <td className="office-table-right-cell">
                                    {renderTextCell(
                                        officeObj.copilotServerName
                                    )}
                                </td>
                            </tr>

                            <tr>
                                <td className="office-table-left-cell">
                                    Super Sync Computer:
                                </td>
                                <td className="office-table-right-cell">
                                    {renderTextCell(
                                        officeObj.superSyncComputer
                                    )}
                                </td>
                            </tr>

                            <tr>
                                <td className="office-table-left-cell">
                                    DXC Account #:
                                </td>
                                <td className="office-table-right-cell">
                                    {renderTextCell(officeObj.dxcAccountNumber)}
                                </td>
                            </tr>
                            <tr>
                                <td className="office-table-left-cell">
                                    EDS Practice ID:
                                </td>
                                <td className="office-table-right-cell">
                                    {renderTextCell(officeObj.edsPracticeId)}
                                </td>
                            </tr>

                            <tr>
                                <td className="office-table-left-cell">
                                    Copilot Notes:
                                </td>
                                <td
                                    className="office-table-right-cell"
                                    style={{ whiteSpace: 'pre-wrap' }}>
                                    {renderTextCell(officeObj.copilotNotes)}
                                </td>
                            </tr>
                        </>
                    )}
                    {officeObj.numOfPPConnections > 0 && <></>}
                </tbody>
            </table>
        </>
    );
};

export default OfficeInfoTable;
